import { useNavigate } from 'react-router-dom';
import Card from '../common/Card';
import Button from '../common/Button';
import { ChartBarIcon, SparklesIcon } from '@heroicons/react/24/outline';
import ValuePlaysInsight from '../analytics/ValuePlaysInsight';

// Helper to get a valid abbreviation or fallback to a short name
const getTeamAbbr = (team) => {
  const abbr = team?.abbreviation || '';
  // Valid abbreviation: 2-6 uppercase letters, possibly with hyphen
  const isValidAbbr = /^[A-Z]{2,6}(-[A-Z]{1,3})?$/.test(abbr);
  if (isValidAbbr) return abbr;
  // Fallback: first 4 chars of name
  const name = team?.name || '';
  return name.substring(0, 4).toUpperCase() || 'TBD';
};

/**
 * GameInsightsSection - Shows key betting insights beneath the main game tile
 * Displays predicted winner, score prediction, streaks, and key advantages
 */
const GameInsightsSection = ({ game }) => {
  // Extract useful data from game object
  const homeTeam = game.home_team || {};
  const awayTeam = game.away_team || {};

  // Check for momentum/streak data
  const homeMomentum = homeTeam.momentum || {};
  const awayMomentum = awayTeam.momentum || {};
  const homeStreak = homeMomentum.current_streak || 0;
  const awayStreak = awayMomentum.current_streak || 0;

  // Get O/U line if available
  const overUnder = game.over_under || game.total;

  // Check if we have any meaningful data to show
  const hasStreakData = homeStreak !== 0 || awayStreak !== 0;
  const hasOverUnder = overUnder !== null && overUnder !== undefined;

  // Don't show section if no data
  if (!hasStreakData && !hasOverUnder) {
    return null;
  }

  // Determine streak badges
  const getStreakDisplay = (streak, teamAbbr) => {
    if (streak === 0) return null;
    const isWin = streak > 0;
    const count = Math.abs(streak);

    if (count >= 5) {
      return {
        icon: isWin ? '🔥' : '❄️',
        label: isWin ? `W${count}` : `L${count}`,
        color: isWin
          ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 border-orange-500/40 text-orange-400'
          : 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-blue-500/40 text-blue-400',
        hot: isWin,
      };
    } else if (count >= 3) {
      return {
        icon: isWin ? '📈' : '📉',
        label: isWin ? `W${count}` : `L${count}`,
        color: isWin
          ? 'bg-green-500/10 border-green-500/30 text-green-400'
          : 'bg-red-500/10 border-red-500/30 text-red-400',
        hot: false,
      };
    }
    return null;
  };

  const homeAbbr = getTeamAbbr(homeTeam);
  const awayAbbr = getTeamAbbr(awayTeam);
  const homeStreakDisplay = getStreakDisplay(homeStreak, homeAbbr);
  const awayStreakDisplay = getStreakDisplay(awayStreak, awayAbbr);

  // Only show if we have something meaningful
  if (!homeStreakDisplay && !awayStreakDisplay && !hasOverUnder) {
    return null;
  }

  return (
    <div className="mt-3 pt-3 border-t border-gray-700/30">
      <div className="flex items-center justify-between gap-2">
        {/* Away Team Streak */}
        <div className="flex-1 min-w-0">
          {awayStreakDisplay ? (
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-semibold ${awayStreakDisplay.color}`}>
              <span>{awayStreakDisplay.icon}</span>
              <span>{awayAbbr} {awayStreakDisplay.label}</span>
            </div>
          ) : (
            <div className="text-xs text-gray-500">{awayAbbr}</div>
          )}
        </div>

        {/* Center - O/U Line if available */}
        {hasOverUnder && (
          <div className="flex-shrink-0 text-center">
            <div className="text-[10px] text-gray-500 uppercase">O/U</div>
            <div className="text-sm font-bold text-white">{overUnder}</div>
          </div>
        )}

        {/* Home Team Streak */}
        <div className="flex-1 min-w-0 text-right">
          {homeStreakDisplay ? (
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-semibold ${homeStreakDisplay.color}`}>
              <span>{homeAbbr} {homeStreakDisplay.label}</span>
              <span>{homeStreakDisplay.icon}</span>
            </div>
          ) : (
            <div className="text-xs text-gray-500">{homeAbbr}</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper to get team ranking (AP rank preferred, then coaches rank)
const getTeamRank = (team) => {
  const rank = team?.ap_rank || team?.coaches_rank;
  return rank && rank <= 25 ? rank : null;
};

// Sports that show rankings
const ncaaSports = ['ncaa_b', 'ncaa-b', 'ncaa_f', 'ncaa-f'];

// NCAA sports that have analytics pages (no box score since no player data)
const ncaaAnalyticsSports = ['ncaa_b', 'ncaa-b'];

const GameCard = ({ game }) => {
  const navigate = useNavigate();

  // Check if this is an NCAA sport that shows rankings
  const showRankings = ncaaSports.includes(game.sport);

  // Get rankings for both teams
  const awayRank = showRankings ? getTeamRank(game.away_team) : null;
  const homeRank = showRankings ? getTeamRank(game.home_team) : null;

  // Safely access predictions (can be multiple)
  const predictions = game.predictions || [];

  // Game status - handle both lowercase and capitalized status values
  const status = (game.status || '').toLowerCase();
  const isLive = status === 'in_progress' || status === 'in progress';
  const isFinal = status === 'final' || status === 'completed' || status === 'finished';
  const isScheduled = !isLive && !isFinal;

  // Format time only
  const formatGameTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Handle box score navigation
  const handleBoxScoreClick = (e) => {
    e.stopPropagation();
    const sport = game.sport || 'nfl';
    const gameId = game.id || game.game_id;
    if (gameId) {
      navigate(`/leagues/${sport}/game/${gameId}/boxscore`);
    }
  };

  // Handle analytics navigation
  const handleAnalyticsClick = (e) => {
    e.stopPropagation();
    const sport = game.sport || 'nfl';
    const gameId = game.id || game.game_id;
    if (gameId) {
      navigate(`/leagues/${sport}/game/${gameId}/analytics`);
    }
  };

  // Filter for ONLY mvp_matrix and forecast_champion predictions
  const validPredictions = predictions.filter(pred => {
    const provider = (pred.ai_provider || '').toLowerCase();
    return provider.includes('mvp_matrix') || provider.includes('forecast_champion');
  });

  // Organize predictions by AI model
  const aiModels = {
    mvp_matrix: {
      name: 'Knewton',
      shortName: 'Knewton',
      color: 'bg-accent-blue',
      textColor: 'text-accent-blue',
      winLoss: null,
      totalScore: null,
      analysis: null
    },
    forecast_champion: {
      name: 'Merlin',
      shortName: 'Merlin',
      color: 'bg-accent-purple',
      textColor: 'text-accent-purple',
      winLoss: null,
      totalScore: null,
      analysis: null
    }
  };

  // Populate predictions for each AI
  validPredictions.forEach(pred => {
    const provider = (pred.ai_provider || '').toLowerCase();
    const result = pred.predicted_result || {};
    const modelKey = provider.includes('mvp_matrix') ? 'mvp_matrix' : 'forecast_champion';

    if (pred.prediction_type === 'win_loss') {
      const predictedWinner = result.predicted_winner;
      const pickedHome = predictedWinner === 'HOME' || predictedWinner === game.home_team?.name;
      aiModels[modelKey].winLoss = {
        winner: pickedHome ? 'home' : 'away',
        confidence: result.confidence,
        predictedScore: result.predicted_score
      };
      aiModels[modelKey].analysis = result.analysis;
    } else if (pred.prediction_type === 'total_score') {
      aiModels[modelKey].totalScore = result.total_score;
    }
  });

  // Check if we have any predictions to show
  const hasPredictions = Object.values(aiModels).some(model => model.winLoss || model.totalScore);

  return (
    <Card className="p-4 relative overflow-hidden hover:shadow-lg transition-all duration-200">
      {/* Status and Time Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-semibold text-text-secondary uppercase tracking-wider">
          {isScheduled && formatGameTime(game.date || game.game_date)}
          {isLive && <span className="text-danger">● LIVE</span>}
          {isFinal && 'FINAL'}
        </div>
      </div>

      {/* Teams and Scores */}
      {/* Mobile: Teams on same line, scores below */}
      <div className="md:hidden">
        {/* Teams Row */}
        <div className="flex items-center justify-between gap-4 mb-3">
          {/* Away Team */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {game.away_team?.logo ? (
              <img src={game.away_team.logo} alt="" className="w-8 h-8 object-contain flex-shrink-0" />
            ) : (
              <div className="w-8 h-8 bg-bg-card-hover rounded flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold">{getTeamAbbr(game.away_team)}</span>
              </div>
            )}
            <span className="text-sm font-bold text-text-primary truncate">
              {awayRank && <span className="text-accent-blue mr-1">#{awayRank}</span>}
              {getTeamAbbr(game.away_team)}
            </span>
          </div>

          <span className="text-xs text-text-secondary font-semibold">@</span>

          {/* Home Team */}
          <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
            <span className="text-sm font-bold text-text-primary truncate">
              {homeRank && <span className="text-accent-blue mr-1">#{homeRank}</span>}
              {getTeamAbbr(game.home_team)}
            </span>
            {game.home_team?.logo ? (
              <img src={game.home_team.logo} alt="" className="w-8 h-8 object-contain flex-shrink-0" />
            ) : (
              <div className="w-8 h-8 bg-bg-card-hover rounded flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold">{getTeamAbbr(game.home_team)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Scores Row */}
        <div className="flex items-center justify-center gap-6">
          <div className={`text-3xl font-black tabular-nums ${
            isFinal && game.away_score > game.home_score ? 'text-success' : 'text-text-primary'
          }`}>
            {game.away_score ?? '-'}
          </div>
          <div className="text-sm text-text-secondary font-semibold">-</div>
          <div className={`text-3xl font-black tabular-nums ${
            isFinal && game.home_score > game.away_score ? 'text-success' : 'text-text-primary'
          }`}>
            {game.home_score ?? '-'}
          </div>
        </div>
      </div>

      {/* Desktop: Original stacked layout */}
      <div className="hidden md:block space-y-2.5">
        {/* Away Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            {game.away_team?.logo ? (
              <img src={game.away_team.logo} alt="" className="w-10 h-10 object-contain flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 bg-bg-card-hover rounded flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold">{getTeamAbbr(game.away_team)}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <span className="text-sm font-bold text-text-primary truncate block">
                {awayRank && <span className="text-accent-blue mr-1">#{awayRank}</span>}
                {game.away_team?.name || 'Away'}
              </span>
            </div>
          </div>
          <div className={`text-2xl font-black tabular-nums ml-3 ${
            isFinal && game.away_score > game.home_score ? 'text-success' : 'text-text-primary'
          }`}>
            {game.away_score ?? '-'}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border/50"></div>

        {/* Home Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            {game.home_team?.logo ? (
              <img src={game.home_team.logo} alt="" className="w-10 h-10 object-contain flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 bg-bg-card-hover rounded flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold">{getTeamAbbr(game.home_team)}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <span className="text-sm font-bold text-text-primary truncate block">
                {homeRank && <span className="text-accent-blue mr-1">#{homeRank}</span>}
                {game.home_team?.name || 'Home'}
              </span>
            </div>
          </div>
          <div className={`text-2xl font-black tabular-nums ml-3 ${
            isFinal && game.home_score > game.away_score ? 'text-success' : 'text-text-primary'
          }`}>
            {game.home_score ?? '-'}
          </div>
        </div>
      </div>

      {/* Game Insights Section - Shows prediction/momentum data beneath teams */}
      {!isFinal && (
        <GameInsightsSection game={game} />
      )}

      {/* Value Plays Section - Shows ML vs Sportsbook value plays */}
      {!isFinal && game.sport && (game.date || game.game_date) && (
        <ValuePlaysInsight
          sport={game.sport}
          gameDate={(game.date || game.game_date)?.split('T')[0]}
          matchup={`${getTeamAbbr(game.away_team)} @ ${getTeamAbbr(game.home_team)}`}
          compact={true}
        />
      )}

      {/* AI Predictions Section - TEMPORARILY DISABLED (will be added based on roles) */}
      {false && hasPredictions && !isFinal && (
        <div className="mt-4 pt-4 border-t border-border space-y-2">
          <div className="text-[10px] font-bold text-text-secondary uppercase tracking-wider mb-2">
            AI Predictions
          </div>

          {Object.entries(aiModels).map(([key, model]) => {
            if (!model.winLoss && !model.totalScore) return null;

            return (
              <div key={key}>
                {/* Prediction Row */}
                <div className={`${model.color} bg-opacity-10 rounded-lg p-2.5 border border-opacity-20 ${model.color.replace('bg-', 'border-')}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={`text-xs font-bold ${model.textColor}`}>
                      {model.name}
                    </span>
                    {model.winLoss?.confidence && (
                      <span className="text-[10px] font-semibold text-text-secondary">
                        {Math.round(model.winLoss.confidence * 10)}% confident
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Win/Loss Pick */}
                    {model.winLoss && (
                      <div className="flex-1">
                        <div className="text-[10px] text-text-secondary mb-0.5">Winner</div>
                        <div className={`text-sm font-bold ${model.textColor}`}>
                          {model.winLoss.winner === 'home'
                            ? game.home_team?.abbreviation || 'HOME'
                            : game.away_team?.abbreviation || 'AWAY'
                          }
                        </div>
                        {model.winLoss.predictedScore && (
                          <div className="text-[10px] text-text-secondary">
                            {model.winLoss.predictedScore}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Total Score */}
                    {model.totalScore && (
                      <div className="flex-1">
                        <div className="text-[10px] text-text-secondary mb-0.5">Total</div>
                        <div className={`text-sm font-bold ${model.textColor}`}>
                          O/U {Math.round(model.totalScore)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Action Buttons - Show for non-NCAA sports + NCAAB (which has analytics) */}
      {(!showRankings || ncaaAnalyticsSports.includes(game.sport)) && (
        <div className="mt-4 pt-3 border-t border-border">
          {isFinal && !ncaaAnalyticsSports.includes(game.sport) ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleBoxScoreClick}
              className="w-full flex items-center justify-center gap-2"
            >
              <ChartBarIcon className="w-4 h-4" />
              <span className="text-xs">View Box Score</span>
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleAnalyticsClick}
              className="w-full flex items-center justify-center gap-2"
            >
              <SparklesIcon className="w-4 h-4" />
              <span className="text-xs">View Analytics</span>
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

export default GameCard;
